import { useOne, useList } from '@refinedev/core';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Loader2,
  BarChart3,
  Tag,
  Users,
  Package,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormHeader } from '@/components/forms';
import {
  EntityAnalyticsCard,
  EntityAnalyticsGrid,
  TabsWithPersistence,
} from '@/components/shared';
import {
  FieldRow,
  FieldGrid,
  formatDate,
} from '@/components/universal/show-utils';
import { PricingTiersSection } from '@/components/pricing-tiers';
import type { TranslationLanguage } from '@/components/translations';
import {
  type SubscriptionPlan,
  getSubscriptionPlanName,
} from './types';

type LanguageRecord = {
  code: string;
  name: string;
  nativeName: string;
  flag?: string | null;
  flagEmoji?: string | null;
};

type SubscriptionRecord = {
  id: number;
  status: string;
};

function getStatusBadge(isActive: boolean) {
  return isActive ? (
    <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
      Активен
    </Badge>
  ) : (
    <Badge variant="outline" className="bg-gray-500/10 text-gray-500 border-gray-500/20">
      Неактивен
    </Badge>
  );
}

// --- Analytics Tab ---

function AnalyticsTab({ plan, subscriptions }: { plan: SubscriptionPlan; subscriptions: SubscriptionRecord[] }) {
  const activeSubscriptions = subscriptions.filter(
    (s) => s.status === 'ACTIVE' || s.status === 'active',
  );
  const totalSubscriptions = subscriptions.length;

  return (
    <div className="mt-6 space-y-6">
      <EntityAnalyticsGrid className="grid-cols-2 lg:grid-cols-3">
        <EntityAnalyticsCard
          title="Активных подписок"
          value={activeSubscriptions.length}
          icon={<Users className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Всего подписок"
          value={totalSubscriptions}
          icon={<Package className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Статус"
          value={plan.isActive ? 'Активен' : 'Неактивен'}
          icon={getStatusBadge(plan.isActive)}
        />
      </EntityAnalyticsGrid>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Основная информация</CardTitle>
        </CardHeader>
        <CardContent>
          <FieldGrid>
            <FieldRow label="Код">
              <span className="font-mono">{plan.code}</span>
            </FieldRow>
            <FieldRow label="Статус">
              {getStatusBadge(plan.isActive)}
            </FieldRow>
          </FieldGrid>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Метаданные</CardTitle>
        </CardHeader>
        <CardContent>
          <FieldGrid>
            <FieldRow label="ID">{plan.id}</FieldRow>
            <FieldRow label="Создан">{formatDate(plan.createdAt)}</FieldRow>
            <FieldRow label="Обновлён">{formatDate(plan.updatedAt)}</FieldRow>
          </FieldGrid>
        </CardContent>
      </Card>
    </div>
  );
}

// --- Pricing Tab ---

function PricingTab({ planId, languages }: { planId: number; languages: TranslationLanguage[] }) {
  return (
    <div className="space-y-4">
      <PricingTiersSection
        productKey={{ subscriptionPlanId: planId }}
        languages={languages}
      />
    </div>
  );
}

// --- Main Show Page ---

export function SubscriptionPlansShow() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { result: plan, query } = useOne<SubscriptionPlan>({
    resource: 'subscription-plans',
    id: id ?? '',
  });

  const { result: languagesResult } = useList<LanguageRecord>({
    resource: 'languages',
  });

  // Подписки, привязанные к этому плану (для аналитики)
  const subscriptions: SubscriptionRecord[] = (plan as SubscriptionPlan & { subscriptions?: SubscriptionRecord[] })?.subscriptions ?? [];

  const languages: TranslationLanguage[] = (languagesResult?.data ?? []).map((l) => ({
    code: l.code,
    name: l.name,
    nativeName: l.nativeName,
    flag: l.flag ?? l.flagEmoji ?? '',
  }));

  if (query.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Абонемент не найден</p>
        <Button variant="link" onClick={() => navigate('/subscription-plans')}>
          Вернуться к списку
        </Button>
      </div>
    );
  }

  const planName = getSubscriptionPlanName(plan);

  return (
    <div className="space-y-6">
      <FormHeader
        title={planName}
        onBack={() => navigate('/subscription-plans')}
        titleAddon={getStatusBadge(plan.isActive)}
        sticky
      />

      <TabsWithPersistence
        tabs={[
          {
            value: 'analytics',
            label: 'Аналитика',
            icon: <BarChart3 className="h-4 w-4" />,
            content: <AnalyticsTab plan={plan} subscriptions={subscriptions} />,
          },
          {
            value: 'pricing',
            label: 'Тарифы',
            icon: <Tag className="h-4 w-4" />,
            content: <PricingTab planId={plan.id} languages={languages} />,
          },
        ]}
        defaultTab="analytics"
      />
    </div>
  );
}
